import useCartPrice from 'api/cartPricing/useCartPrice';
import type { ContinuousSelection } from 'utils/continuousSelection';
import { useMemo } from 'react';
import {
  buildStayQuoteBookings,
  summariseStayQuote,
  type StayQuote,
  type StayQuoteTier,
} from 'utils/cart/stayQuote';

interface UseStayQuoteOptions {
  enabled: boolean;
  experienceId?: string;
  selection: ContinuousSelection | null;
  tiers: readonly StayQuoteTier[];
}

/**
 * The server's price for the stay a guest has picked, before anything is in the cart.
 *
 * Priced by the same endpoint that prices the cart, so a weekend rate on the second night or a fee
 * levied per date shows on the tier line and in the summary exactly as it will be charged.
 */
const useStayQuote = ({
  enabled,
  experienceId,
  selection,
  tiers,
}: UseStayQuoteOptions): { isLoading: boolean; quote: StayQuote | null } => {
  const bookings = useMemo(
    () =>
      enabled && selection && experienceId && tiers.length > 0
        ? buildStayQuoteBookings({
            durationMinutes: selection.durationMinutes,
            experienceId,
            startDateTime: selection.startDateTime,
            tiers,
          })
        : [],
    [enabled, experienceId, selection, tiers],
  );

  const { cart, isLoading } = useCartPrice({ bookings });

  const quote = useMemo(
    () => (bookings.length > 0 ? summariseStayQuote(cart, tiers) : null),
    [bookings.length, cart, tiers],
  );

  return { isLoading, quote };
};

export default useStayQuote;
